import { useState } from "react";
import SectionTitle from "@/components/SectionTitle";
import StockSearch from "@/components/StockSearch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calculator, Plus, Trash2, Layers, Wallet, Target, Coins } from "lucide-react";

const idr = (n) => (isFinite(n) ? "Rp " + Math.round(n).toLocaleString("id-ID") : "-");
const num = (v) => parseFloat(String(v).replace(/[^0-9.-]/g, "")) || 0;

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-xs text-[var(--text-secondary)] mb-1.5 block">{label}</label>
    <Input {...props} className="bg-[var(--surface-base)] border-[var(--border)] font-mono-data focus-visible:ring-[var(--accent)] h-10" />
  </div>
);

const Result = ({ label, value, accent }) => (
  <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
    <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">{label}</p>
    <p className={`font-mono-data font-bold text-lg ${accent || ""}`}>{value}</p>
  </div>
);

function AverageDown() {
  const [rows, setRows] = useState([{ price: "", lot: "" }, { price: "", lot: "" }]);
  const set = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const add = () => setRows((r) => [...r, { price: "", lot: "" }]);
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const totalShares = rows.reduce((a, r) => a + num(r.lot) * 100, 0);
  const totalCost = rows.reduce((a, r) => a + num(r.price) * num(r.lot) * 100, 0);
  const avg = totalShares ? totalCost / totalShares : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex items-end gap-2" data-testid={`avg-row-${i}`}>
            <div className="flex-1"><Field label={`Harga Beli #${i + 1}`} type="number" value={r.price} onChange={(e) => set(i, "price", e.target.value)} placeholder="0" data-testid={`avg-price-${i}`} /></div>
            <div className="flex-1"><Field label="Lot" type="number" value={r.lot} onChange={(e) => set(i, "lot", e.target.value)} placeholder="0" data-testid={`avg-lot-${i}`} /></div>
            {rows.length > 1 && (
              <button onClick={() => del(i)} className="h-10 w-10 flex items-center justify-center rounded-md bg-[var(--surface-hover)] text-bear hover:bg-[var(--bear)]/15" data-testid={`avg-del-${i}`}><Trash2 size={15} /></button>
            )}
          </div>
        ))}
        <Button onClick={add} variant="outline" data-testid="avg-add-row" className="w-full border-dashed border-[var(--border)] bg-transparent hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"><Plus size={15} className="mr-1.5" /> Tambah Pembelian</Button>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Result label="Total Lot" value={(totalShares / 100).toLocaleString("id-ID")} />
          <Result label="Total Lembar" value={totalShares.toLocaleString("id-ID")} />
        </div>
        <Result label="Total Modal" value={idr(totalCost)} />
        <div className="card p-4 border-[var(--accent)]/30 bg-[var(--accent)]/5">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Harga Rata-Rata (Breakeven)</p>
          <p className="font-mono-data font-extrabold text-2xl text-accent" data-testid="avg-result">{avg ? Math.round(avg).toLocaleString("id-ID") : "-"}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">Harga jual di atas nilai ini = profit.</p>
        </div>
      </div>
    </div>
  );
}

function MoneyManagement() {
  const [capital, setCapital] = useState("");
  const [risk, setRisk] = useState("2");
  const [entry, setEntry] = useState("");
  const [sl, setSl] = useState("");

  const riskIDR = num(capital) * (num(risk) / 100);
  const riskPerShare = num(entry) - num(sl);
  const maxShares = riskPerShare > 0 ? Math.floor(riskIDR / riskPerShare) : 0;
  const lots = Math.floor(maxShares / 100);
  const posValue = lots * 100 * num(entry);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <Field label="Modal Total (Rp)" type="number" value={capital} onChange={(e) => setCapital(e.target.value)} placeholder="10000000" data-testid="mm-capital" />
        <Field label="Risiko Maks per Trade (%)" type="number" value={risk} onChange={(e) => setRisk(e.target.value)} placeholder="2" data-testid="mm-risk" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Harga Entry" type="number" value={entry} onChange={(e) => setEntry(e.target.value)} placeholder="0" data-testid="mm-entry" />
          <Field label="Stop Loss" type="number" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="0" data-testid="mm-sl" />
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Result label="Risiko (Rp)" value={idr(riskIDR)} accent="text-bear" />
          <Result label="Risiko / Lembar" value={riskPerShare > 0 ? idr(riskPerShare) : "-"} />
        </div>
        <div className="card p-4 border-[var(--accent)]/30 bg-[var(--accent)]/5">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Ukuran Posisi Maksimal (1 lot = 100 lembar)</p>
          <p className="font-mono-data font-extrabold text-2xl text-accent" data-testid="mm-lots">{lots.toLocaleString("id-ID")} Lot</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">= {(lots * 100).toLocaleString("id-ID")} lembar</p>
        </div>
        <Result label="Nilai Posisi" value={idr(posValue)} />
      </div>
    </div>
  );
}

function TargetTP() {
  const [entry, setEntry] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [lot, setLot] = useState("");

  const shares = num(lot) * 100;
  const rewardShare = num(tp) - num(entry);
  const riskShare = num(entry) - num(sl);
  const rewardIDR = rewardShare * shares;
  const riskIDR = riskShare * shares;
  const rrr = riskShare > 0 ? rewardShare / riskShare : 0;
  const gainPct = num(entry) ? (rewardShare / num(entry)) * 100 : 0;
  const lossPct = num(entry) ? (riskShare / num(entry)) * 100 : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Harga Entry" type="number" value={entry} onChange={(e) => setEntry(e.target.value)} placeholder="0" data-testid="tp-entry" />
          <Field label="Jumlah Lot" type="number" value={lot} onChange={(e) => setLot(e.target.value)} placeholder="0" data-testid="tp-lot" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target Price (TP)" type="number" value={tp} onChange={(e) => setTp(e.target.value)} placeholder="0" data-testid="tp-target" />
          <Field label="Stop Loss (Cut)" type="number" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="0" data-testid="tp-sl" />
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Result label="Potensi Profit" value={idr(rewardIDR)} accent="text-bull" />
          <Result label="Potensi Loss" value={idr(riskIDR)} accent="text-bear" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Result label="Gain %" value={gainPct ? `+${gainPct.toFixed(2)}%` : "-"} accent="text-bull" />
          <Result label="Loss %" value={lossPct ? `-${lossPct.toFixed(2)}%` : "-"} accent="text-bear" />
        </div>
        <div className="card p-4 border-[var(--accent)]/30 bg-[var(--accent)]/5">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Risk / Reward Ratio</p>
          <p className="font-mono-data font-extrabold text-2xl text-accent" data-testid="tp-rrr">{rrr ? `1 : ${rrr.toFixed(2)}` : "-"}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">{rrr >= 2 ? "Rasio bagus, layak dieksekusi." : rrr > 0 ? "Rasio kurang ideal (<1:2)." : "Isi data untuk menghitung."}</p>
        </div>
      </div>
    </div>
  );
}

function Dividend() {
  const [price, setPrice] = useState("");
  const [dps, setDps] = useState("");
  const [lot, setLot] = useState("");
  const [picked, setPicked] = useState(null);
  const [info, setInfo] = useState(null);
  const [loadingSym, setLoadingSym] = useState(false);

  const shares = num(lot) * 100;
  const totalDiv = num(dps) * shares;
  const invest = num(price) * shares;
  const yieldPct = num(price) ? (num(dps) / num(price)) * 100 : 0;

  const fmtDate = (s) => {
    if (!s) return "-";
    const M = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
    const d = new Date(s + "T00:00:00");
    return `${d.getDate()} ${M[d.getMonth()]} ${d.getFullYear()}`;
  };

  const onSelect = async (code) => {
    setLoadingSym(true);
    setPicked(code);
    setInfo(null);
    try {
      const res = await api.get(`/dividend/${code}`);
      const d = res.data || {};
      if (d.price != null) setPrice(String(d.price));
      if (d.dividend != null) setDps(String(d.dividend));
      setPicked(d.symbol || code);
      setInfo(d);
      if (d.dividend == null) toast.info(`${code}: data dividen belum tersedia, isi manual ya.`);
      else toast.success(`${code}: dividen Rp ${Number(d.dividend).toLocaleString("id-ID")}/lembar dimuat.`);
    } catch (e) {
      toast.error("Gagal memuat data emiten.");
    } finally {
      setLoadingSym(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-[var(--text-secondary)] mb-1.5 block">Cari Emiten (auto-isi harga & dividen)</label>
          <StockSearch onSelect={onSelect} />
          {picked && (
            <p className="text-[11px] text-[var(--text-muted)] mt-1.5" data-testid="div-picked">
              {loadingSym ? "Memuat data…" : <>Terpilih: <span className="text-accent font-semibold">{picked}</span> · harga & dividen per lembar terisi otomatis (bisa diubah manual).</>}
            </p>
          )}
        </div>
        <Field label="Harga Saham (Rp)" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" data-testid="div-price" />
        <Field label="Dividen per Lembar (Rp)" type="number" value={dps} onChange={(e) => setDps(e.target.value)} placeholder="0" data-testid="div-dps" />
        <Field label="Jumlah Lot" type="number" value={lot} onChange={(e) => setLot(e.target.value)} placeholder="0" data-testid="div-lot" />
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Result label="Total Investasi" value={idr(invest)} />
          <Result label="Dividend Yield" value={yieldPct ? `${yieldPct.toFixed(2)}%` : "-"} accent="text-accent" />
        </div>
        <div className="card p-4 border-[var(--accent)]/30 bg-[var(--accent)]/5">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Total Dividen Diterima</p>
          <p className="font-mono-data font-extrabold text-2xl text-accent" data-testid="div-total">{idr(totalDiv)}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">Estimasi kotor (belum termasuk PPh final 10% dividen tunai).</p>
        </div>
        <Result label="Setelah PPh 10%" value={idr(totalDiv * 0.9)} accent="text-bull" />
        {info && info.ex_dividend_date && (
          <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]" data-testid="div-exdate">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Cum-Date (Batas Beli)</p>
                <p className="font-mono-data font-bold text-sm text-amber">{fmtDate(info.cum_date)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Ex-Date</p>
                <p className="font-mono-data font-bold text-sm">{fmtDate(info.ex_dividend_date)}</p>
              </div>
            </div>
            <p className="text-[11px] text-[var(--text-secondary)] mt-2">
              {info.ex_upcoming
                ? <>Beli & pegang saham paling lambat <span className="text-amber font-semibold">cum-date</span> untuk berhak atas dividen ini.</>
                : <>Ini jadwal ex-dividend <span className="font-semibold">terakhir</span> (sudah lewat) — perkiraan; pantau pengumuman resmi untuk periode berikutnya.</>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: "dividend", label: "Dividen", icon: Coins, comp: Dividend },
  { id: "average", label: "Average Down", icon: Layers, comp: AverageDown },
  { id: "money", label: "Money Management", icon: Wallet, comp: MoneyManagement },
  { id: "target", label: "Target & TP", icon: Target, comp: TargetTP },
];

export default function Calculators() {
  return (
    <section id="calculator" className="scroll-mt-20">
      <SectionTitle
        eyebrow="4 Modul Presisi"
        title="Kalkulator Saham Pintar"
        desc="Hitung averaging, ukuran posisi sesuai manajemen risiko, target profit, dan proyeksi dividen — akurat sesuai aturan lot IDX."
        icon={Calculator}
      />
      <div className="card p-5 lg:p-6">
        <Tabs defaultValue="dividend">
          <TabsList className="bg-[var(--surface-base)] border border-[var(--border)] p-1 h-auto flex-wrap gap-1 mb-6">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`calc-tab-${t.id}`} className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[#07090E] text-[var(--text-secondary)] px-4 py-2 text-sm font-medium">
                <t.icon size={15} className="mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              <t.comp />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
